import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Video, Image as ImageIcon, X, Play, CheckCircle2, Package } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminListMedia, adminCreateMedia, adminUpdateMedia, adminDeleteMedia, adminUploadImage } from "../lib/api";

/**
 * Preuves d'envoi — panneau dédié.
 * L'admin dépose une ou plusieurs photos/vidéos dans une sous-section
 * et elles sont publiées instantanément sur la page À propos (Chapitre VI).
 */

const SECTIONS = [
  { key: "general", label: "Général", flag: "📦", desc: "Colis sans transporteur précis" },
  { key: "colissimo", label: "Colissimo", flag: "📮", desc: "Colissimo 48h Domicile" },
  { key: "colis_prive", label: "Colis Privé", flag: "🚚", desc: "Colis Privé domicile" },
  { key: "mondial_relay", label: "Mondial Relay", flag: "🏪", desc: "Point relais / Locker" },
  { key: "tiktok_fr", label: "TikTok Shop France", flag: "🇫🇷", desc: "Livraisons TikTok Shop FR" },
  { key: "tiktok_it", label: "TikTok Shop Italie", flag: "🇮🇹", desc: "Livraisons TikTok Shop IT" },
  { key: "tiktok_es", label: "TikTok Shop Espagne", flag: "🇪🇸", desc: "Livraisons TikTok Shop ES" },
];

const AdminEnvois = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");
  const [uploads, setUploads] = useState({}); // filename -> {status, progress}
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const all = await adminListMedia();
      setItems(all.filter((m) => m.category === "envois"));
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const currentSection = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];

  const itemsForTab = useMemo(
    () => items.filter((m) => (m.subcategory || "general") === tab),
    [items, tab]
  );

  const countsBySection = useMemo(() => {
    const counts = {};
    for (const s of SECTIONS) counts[s.key] = 0;
    for (const m of items) {
      const k = m.subcategory || "general";
      counts[k] = (counts[k] || 0) + 1;
    }
    return counts;
  }, [items]);

  const uploadOne = async (file) => {
    const trackId = `${file.name}-${Date.now()}`;
    setUploads((u) => ({ ...u, [trackId]: { name: file.name, progress: 0, status: "uploading" } }));

    try {
      // 1) send file to storage
      const uploaded = await adminUploadImage(file, (p) => {
        setUploads((u) => ({ ...u, [trackId]: { ...u[trackId], progress: p } }));
      });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${base}${uploaded.url}`;

      // 2) auto-publish immediately (active=true, category=envois, subcategory=current tab)
      const auto_title = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
      const media = await adminCreateMedia({
        title: auto_title || `Envoi ${new Date().toLocaleDateString("fr-FR")}`,
        description: "",
        media_url: url,
        media_type: uploaded.is_video ? "video" : "photo",
        category: "envois",
        subcategory: tab,
        featured: false,
        active: true,
      });
      setItems((prev) => [media, ...prev]);
      setUploads((u) => ({ ...u, [trackId]: { ...u[trackId], status: "done", progress: 100 } }));
      // auto-clear success after 3s
      setTimeout(() => {
        setUploads((u) => { const c = { ...u }; delete c[trackId]; return c; });
      }, 3000);
    } catch (e) {
      setUploads((u) => ({ ...u, [trackId]: { ...u[trackId], status: "error", error: e?.response?.data?.detail || "Échec" } }));
      toast.error(`${file.name} : ${e?.response?.data?.detail || "upload échoué"}`);
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    toast.info(`Publication de ${list.length} fichier${list.length > 1 ? "s" : ""} dans « ${currentSection.label} »`);
    // Upload in parallel (up to 3 at a time to avoid saturating)
    const queue = [...list];
    const worker = async () => {
      while (queue.length) {
        const f = queue.shift();
        if (f) await uploadOne(f);
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    toast.success("Publication terminée");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer « ${m.title} » ?`)) return;
    try {
      await adminDeleteMedia(m.id);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Supprimé");
    } catch { toast.error("Suppression impossible"); }
  };

  const toggleActive = async (m) => {
    try {
      const updated = await adminUpdateMedia(m.id, { active: !m.active });
      setItems((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
    } catch { toast.error("Erreur"); }
  };

  const moveTo = async (m, newSubcategory) => {
    try {
      const updated = await adminUpdateMedia(m.id, { subcategory: newSubcategory });
      setItems((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      toast.success(`Déplacé vers « ${SECTIONS.find((s) => s.key === newSubcategory)?.label} »`);
    } catch { toast.error("Déplacement impossible"); }
  };

  const activeUploads = Object.entries(uploads);

  return (
    <AdminLayout current="envois">
      <div className="max-w-7xl" data-testid="admin-envois-page">
        <div className="mb-8">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">
            <Package size={12} className="inline mr-2" /> Preuves d'envoi
          </p>
          <h1 className="font-serif text-4xl md:text-5xl mb-3">Publier des colis</h1>
          <p className="text-white/50 text-sm max-w-2xl leading-relaxed">
            Sélectionnez un transporteur ou une marketplace, glissez vos photos et vidéos de colis :
            elles sont publiées <span className="text-[color:var(--gold)]">immédiatement</span> sur la
            page À propos (Chapitre VI · <em>Preuves d'envoi</em>).
          </p>
        </div>

        {/* Section tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-8" data-testid="envois-tabs">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              className={`text-left p-4 border transition-colors ${
                tab === s.key
                  ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10"
                  : "border-white/10 hover:border-white/30 bg-white/[0.01]"
              }`}
              data-testid={`envois-tab-${s.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl leading-none">{s.flag}</span>
                <span className={`label-caps text-[10px] tracking-[0.2em] ${tab === s.key ? "text-[color:var(--gold)]" : "text-white/80"}`}>
                  {s.label}
                </span>
              </div>
              <p className="text-[10px] text-white/40 leading-tight mb-1">{s.desc}</p>
              <p className="font-serif text-xl text-white">{countsBySection[s.key] || 0}</p>
            </button>
          ))}
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed transition-colors mb-6 p-8 md:p-12 text-center ${
            dragOver ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-white/15 hover:border-white/30"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="envois-dropzone"
        >
          <input
            ref={fileRef} type="file" hidden multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/gif,image/avif,video/mp4,video/quicktime,video/webm"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 text-[color:var(--gold)]">
              <Upload size={32} strokeWidth={1.5} />
              <span className="text-2xl leading-none">{currentSection.flag}</span>
            </div>
            <p className="font-serif text-xl text-white">
              Déposer photos & vidéos dans <span className="text-[color:var(--gold)]">« {currentSection.label} »</span>
            </p>
            <p className="text-white/50 text-xs max-w-md">
              Photos (JPG, PNG, WebP, HEIC, jusqu'à 5 Mo) · Vidéos (MP4, MOV, WebM, jusqu'à 100 Mo).
              Chaque fichier est publié automatiquement dès la fin de l'upload.
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-3 bg-[color:var(--gold)] text-black hover:bg-white px-6 py-2.5 label-caps text-[10px] tracking-[0.25em]"
              data-testid="envois-choose-files"
            >
              Choisir les fichiers
            </button>
            <p className="text-white/30 text-[10px]">💡 Sur mobile : bouton d'appareil photo pour filmer directement</p>
          </div>
        </div>

        {/* Live upload progress */}
        {activeUploads.length > 0 && (
          <div className="border border-white/10 p-4 mb-6 space-y-2" data-testid="upload-progress-panel">
            <p className="label-caps text-[10px] text-white/50 tracking-[0.25em] mb-2">En cours de publication</p>
            {activeUploads.map(([id, u]) => (
              <div key={id} className="flex items-center gap-3 text-xs">
                {u.status === "uploading" && <Loader2 size={12} className="text-[color:var(--gold)] animate-spin" />}
                {u.status === "done" && <CheckCircle2 size={12} className="text-emerald-400" />}
                {u.status === "error" && <X size={12} className="text-red-400" />}
                <span className="flex-1 truncate text-white/80">{u.name}</span>
                <span className="text-white/40 w-12 text-right">{u.progress}%</span>
                <div className="w-32 h-1 bg-white/10">
                  <div
                    className={`h-full transition-all ${u.status === "error" ? "bg-red-400" : "bg-[color:var(--gold)]"}`}
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid of items in this section */}
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif text-2xl">
            {currentSection.flag} {currentSection.label}
            <span className="text-white/40 text-base ml-3">— {itemsForTab.length} envoi{itemsForTab.length > 1 ? "s" : ""}</span>
          </h2>
        </div>

        {loading ? (
          <p className="text-white/40">Chargement…</p>
        ) : itemsForTab.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10">
            <p className="text-white/50">Aucune preuve d'envoi dans cette section.</p>
            <p className="text-white/30 text-xs mt-1">Glissez vos premiers colis ci-dessus.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="envois-grid">
            {itemsForTab.map((m) => (
              <div key={m.id} className={`border ${m.active ? "border-white/10" : "border-white/5 opacity-40"} bg-[#0a0a0a] group relative`} data-testid={`envoi-${m.id.slice(0, 8)}`}>
                <div className="aspect-square bg-black overflow-hidden relative">
                  {m.media_type === "video" ? (
                    <>
                      <video src={m.media_url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors pointer-events-none">
                        <div className="w-10 h-10 rounded-full bg-[color:var(--gold)]/90 flex items-center justify-center">
                          <Play size={14} className="text-black ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={m.media_url} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <span className="absolute top-2 left-2 bg-black/70 label-caps text-[9px] text-white/80 px-2 py-0.5 tracking-[0.2em] flex items-center gap-1">
                    {m.media_type === "video" ? <Video size={9} /> : <ImageIcon size={9} />}
                    {m.media_type === "video" ? "Vidéo" : "Photo"}
                  </span>
                  {!m.active && <span className="absolute top-2 right-2 bg-black/70 label-caps text-[9px] text-orange-300 px-2 py-0.5">CACHÉ</span>}
                </div>
                <div className="p-2.5">
                  <p className="text-[11px] text-white/90 truncate font-medium" title={m.title}>{m.title}</p>
                  <p className="text-[9px] text-white/30 mt-0.5">{new Date(m.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</p>
                  <div className="flex items-center justify-between mt-2 gap-1">
                    <select
                      value={m.subcategory || "general"}
                      onChange={(e) => moveTo(m, e.target.value)}
                      className="flex-1 bg-transparent border border-white/10 text-[9px] px-1 py-0.5 text-white/70"
                      data-testid={`move-${m.id.slice(0, 8)}`}
                      title="Déplacer vers…"
                    >
                      {SECTIONS.map((s) => <option key={s.key} value={s.key} className="bg-[#0a0a0a]">{s.label}</option>)}
                    </select>
                    <button
                      onClick={() => toggleActive(m)}
                      className={`label-caps text-[8px] px-1.5 py-0.5 border ${m.active ? "border-emerald-400/40 text-emerald-400" : "border-orange-400/40 text-orange-400"}`}
                      title={m.active ? "Publié" : "Masqué"}
                      data-testid={`toggle-${m.id.slice(0, 8)}`}
                    >
                      {m.active ? "ON" : "OFF"}
                    </button>
                    <button onClick={() => remove(m)} className="text-white/30 hover:text-red-400 p-1" title="Supprimer" data-testid={`del-${m.id.slice(0, 8)}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEnvois;
